import React, { useEffect, useState } from "react";

import classnames from "classnames";
import { isEmpty } from "ramda";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

import referenceSourceApi from "apis/reference_sources";
import referenceSourcesExploreApi from "apis/referenceSourcesExplore";
import {
  Button,
  Pane,
  Typography,
  Table,
  Checkbox,
  Label,
  Select,
} from "clearsense-ui/components";

import { CROSSWALK_REFERENCE_SOURCE_COLUMNS } from "../../../constants/crosswalkReferenceSource";

const NewCrosswalk = ({ isPaneOpen, setIsPaneOpen }) => {
  const { referenceSourceId } = useParams();
  const [outReferenceSource, setOutReferenceSource] = useState({});
  const [referenceSourceList, setReferenceSourceList] = useState([]);
  const [crosswalkList, setCrosswalkList] = useState([]);
  const [selected, setSelected] = useState({});
  const [isAutomaticMapping, setIsAutomaticMapping] = useState(false);
  const navigate = useNavigate();

  const fetchOutReferenceSource = async () => {
    try {
      const { data } = await referenceSourceApi.show(referenceSourceId);
      setOutReferenceSource(data);
      setCrosswalkList([
        ...data.crosswalks_from_ids.map(({ id }) => id),
        ...data.crosswalks_to_ids.map(({ id }) => id),
      ]);
    } catch (error) {
      logger.error(error);
    }
  };

  const fetchReferenceSources = async () => {
    try {
      const { data } = await referenceSourceApi.index();
      setReferenceSourceList(
        data?.reference_sources?.filter(({ id }) => id !== referenceSourceId)
      );
    } catch (error) {
      logger.error(error);
    }
  };

  useEffect(() => {
    if (isPaneOpen) {
      fetchOutReferenceSource();
      fetchReferenceSources();
    }
  }, [isPaneOpen]);

  const filterRowData = () => {
    const rowData = referenceSourceList.map(referenceSource => {
      if (crosswalkList.includes(referenceSource.id)) {
        referenceSource.disabled = true;
      }

      return referenceSource;
    });
    return rowData;
  };

  const handleSave = async () => {
    try {
      const { data } = await referenceSourcesExploreApi.createCrosswalk(
        referenceSourceId,
        {
          crosswalk_type: "others",
          reference_source_from_id: referenceSourceId,
          reference_source_to_id: selected.id,
          is_automatic_mapping_checked: isAutomaticMapping,
        }
      );
      navigate(`${data?.crosswalk?.id}/crosswalk-mapping`);
    } catch (error) {
      logger.error(error);
    }
  };

  const handleClose = () => {
    setOutReferenceSource({});
    setReferenceSourceList([]);
    setCrosswalkList([]);
    setSelected({});
    setIsAutomaticMapping(false);
    setIsPaneOpen(false);
  };

  return (
    <Pane
      isOpen={isPaneOpen}
      onClose={handleClose}
      size="md"
      data-testid="create-pane"
    >
      <Pane.Header>
        <Typography style="h3" type="bold" className="-mt-3">
          Create Crosswalk
        </Typography>
        <Typography style="large">
          Create a new mapping between on reference source and another
        </Typography>
      </Pane.Header>
      <Pane.Body className="py-8">
        <div className="w-full pb-4">
          <Select
            name="type"
            strategy="fixed"
            label="Out Reference Source"
            placeholder={outReferenceSource.name}
            data-cy="out-reference-source-select-dropdown"
            isDisabled
          />
        </div>
        <Label>In Reference Source</Label>
        <Table
          fixedHeight
          rowClassName={(record, index) =>
            classnames({
              "bg-indigo-100 dark:bg-grey-medium-shade text-indigo-600 font-bold":
                index === selected.index,
              "cursor-pointer dark:bg-cs-ui-gray-800 dark:text-cs-ui-gray-400":
                index !== selected.index,
              "bg-gray-200 dark:bg-cs-ui-gray-800 dark:text-cs-ui-gray-400 pointer-events-none cursor-not-allowed dark:opacity-95":
                record?.disabled,
            })
          }
          rowKey={record => record.id}
          onRowClick={(_, record, rowIndex) =>
            !record?.disabled &&
            setSelected({ index: rowIndex, id: record?.id })
          }
          rowData={filterRowData()}
          columnData={CROSSWALK_REFERENCE_SOURCE_COLUMNS}
          data-cy="reference-source-list-table"
        />
        <Checkbox
          className="pt-4"
          name="mapMatchingValues"
          label="Automatically map matching values between sources"
          data-cy="map-matching-values-check-box"
          checked={isAutomaticMapping}
          onChange={() => setIsAutomaticMapping(prev => !prev)}
        />
      </Pane.Body>
      <Pane.Footer className="items-center gap-2">
        <Button
          label="Save and Configure Crosswalk"
          onClick={handleSave}
          data-cy="save-configure-crosswalk-button"
          disabled={isEmpty(selected)}
          data-testid="save-button"
        />
        <Button
          style="text"
          label="Cancel"
          onClick={handleClose}
          data-cy="create-crosswalk-cancel-button"
          data-testid="cancel-button"
        />
      </Pane.Footer>
    </Pane>
  );
};

export default NewCrosswalk;
